import { toast } from 'react-toastify';
import { useState, useEffect, useMemo } from 'react';
import { CreditAnalysis } from './types';
import { ColumnDef } from './components/DataTable.types';
import ApprovalModal from './ApprovalModal';
import UnifiedDraweeModal from './UnifiedDraweeModal';
import PageFooter from '../../molecules/PageFooter';
import appConfig from '../../../config/app-config';
import DataTable from './components/DataTable';
import { saveDecision, getFeatureFlags } from '../../../services/CreditAnalysisService';
import { buildAnalysisColumns } from './shared/analysisColumns';
import { exportAnalysisToExcel } from './shared/exportToExcel';
import { parseRangeFilter } from './shared/parseRangeFilter';
import { useDebounce } from './useDebounce';

const { address: CAE_API_URL, key: CAE_API_KEY } = appConfig.apis.creditAnalysisApiEnv;

export default function HistoryView() {
    const [currentPage, setCurrentPage] = useState(0);
    const [currentPageSize, setCurrentPageSize] = useState(100);
    const [filters, setFilters] = useState<Record<string, string>>(() => {
        const hash = window.location.hash;
        const queryString = hash.includes('?') ? hash.split('?')[1] : '';
        const searchParams = new URLSearchParams(queryString);
        const initial: Record<string, string> = {};
        const batch = searchParams.get('batch');
        const document = searchParams.get('document');
        if (batch) initial.batchNumber = batch;
        if (document) initial.document = document;
        return initial;
    });
    const [sortField, setSortField] = useState('date');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
    const [showFilters, setShowFilters] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedAnalysis, setSelectedAnalysis] = useState<CreditAnalysis | null>(null);
    const [analysisData, setAnalysisData] = useState<CreditAnalysis[]>([]);
    const [totalFromApi, setTotalFromApi] = useState(0);
    const [draweeModalDoc, setDraweeModalDoc] = useState<string | null>(null);
    const [draweeModalItemId, setDraweeModalItemId] = useState<string | null>(null);
    const [draweeModalName, setDraweeModalName] = useState<string | null>(null);
    const [draweeModalBatchId, setDraweeModalBatchId] = useState<string | null>(null);
    const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({});

    useEffect(() => { getFeatureFlags().then(setFeatureFlags).catch(() => {}); }, []);

    const serverFilterKey = useDebounce(
        JSON.stringify({ b: filters.batchNumber || '', a: filters.assignor || '', d: filters.drawee || '', doc: filters.document || '' })
    );

    useEffect(() => {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (CAE_API_KEY) headers['x-api-key'] = CAE_API_KEY;

        const params = new URLSearchParams();
        params.set('page', String(currentPage));
        params.set('size', String(currentPageSize));
        const parsed = JSON.parse(serverFilterKey);
        if (parsed.b) params.set('batchNumber', parsed.b);
        if (parsed.a) params.set('assignor', parsed.a);
        if (parsed.d) params.set('drawee', parsed.d);
        if (parsed.doc) params.set('document', parsed.doc);

        fetch(`${CAE_API_URL}/analysis-items?${params.toString()}`, { headers })
            .then(res => res.json())
            .then(data => { setAnalysisData(data.content || []); setTotalFromApi(data.totalElements || 0); })
            .catch(() => { toast.error('Erro ao carregar dados'); setAnalysisData([]); });
    }, [currentPage, currentPageSize, serverFilterKey]);



    const { filteredData, totalElements } = useMemo(() => {
        let filtered = [...analysisData];
        const multiselectFields = new Set(['rating', 'recommendation', 'status', 'type']);
        const rangeFields = new Set(['originalValue', 'approvedValue', 'suggestedValue', 'currentExposure', 'potentialExposure', 'scrTotal', 'scrRural', 'overdue', 'scrPrejuizo', 'qsa', 'annualRevenue', 'equity', 'score', 'agroScore', 'pdef', 'pdefAgro', 'scoreQuod', 'scoreBoaVista', 'ccirHa', 'ccirValue', 'patrimonioScr']);
        const displayedInThousands = new Set(['scrTotal', 'scrRural', 'overdue', 'scrPrejuizo', 'qsa']);

        Object.entries(filters).forEach(([field, value]) => {
            if (!value) return;
            if (['batchNumber', 'assignor', 'drawee', 'document'].includes(field)) return; // server-side
            if (rangeFields.has(field)) {
                const predicate = parseRangeFilter(value);
                if (!predicate) return;
                filtered = filtered.filter(item => {
                    const raw = (item as any)[field];
                    let num = parseFloat(String(raw || '0').replace(/[^\d.,-]/g, '').replace(',', '.'));
                    if (displayedInThousands.has(field)) num = num / 1000;
                    return !isNaN(num) && predicate(num);
                });
                return;
            }
            const vals = value.split(';').map(v => v.trim().toLowerCase()).filter(Boolean);
            const isMultiselect = multiselectFields.has(field);
            filtered = filtered.filter(item => {
                const iv = (item as any)[field];
                if (!iv) return isMultiselect && vals.includes('-');
                const low = String(iv).toLowerCase();
                return isMultiselect ? vals.some(v => v === low) : vals.some(v => low.includes(v));
            });
        });

        if (sortField) {
            filtered.sort((a, b) => {
                const cmp = String((a as any)[sortField] || '').localeCompare(String((b as any)[sortField] || ''), 'pt-BR', { numeric: true });
                return sortOrder === 'ASC' ? cmp : -cmp;
            });
        }

        return { filteredData: filtered, totalElements: totalFromApi };
    }, [filters, sortField, sortOrder, analysisData, totalFromApi]);

    const handleSort = (field: string) => {
        if (sortField === field) setSortOrder(o => o === 'ASC' ? 'DESC' : 'ASC');
        else { setSortField(field); setSortOrder('ASC'); }
    };

    const columns = useMemo<ColumnDef<CreditAnalysis>[]>(() => buildAnalysisColumns({
        onOpenModal: (row) => { setSelectedAnalysis(row); setIsModalOpen(true); },
        onOpenDrawee: (doc, itemId, name, batchId) => { setDraweeModalDoc(doc); setDraweeModalItemId(itemId); setDraweeModalName(name); setDraweeModalBatchId(batchId); },
        onSaveDecision: saveDecision,
        onUpdateRow: (uuid, updates) => setAnalysisData(prev => prev.map(i => i.uuid === uuid ? { ...i, ...updates } as CreditAnalysis : i)),
        featureFlags,
        hideBatchColumn: false,
    }), [featureFlags]);

    return (
        <div className="px-4 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 h-full flex flex-col">
            {/* Action bar */}
            <div className="mt-4 mb-2 flex items-center justify-end gap-3 p-2 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Ações em Lote:</span>
                <button onClick={() => setShowFilters(f => !f)} className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded text-sm font-medium">
                    {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
                </button>
                <button onClick={() => setFilters({})} className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded text-sm font-medium">
                    Limpar Filtros
                </button>
                <button onClick={() => exportAnalysisToExcel(filteredData)} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-sm font-medium">
                    Exportar Excel
                </button>
                {filters.batchNumber && (
                    <button className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded text-sm font-medium">
                        Vincular Número de Contrato
                    </button>
                )}
            </div>

            <DataTable<CreditAnalysis>
                columns={columns}
                data={filteredData}
                rowKey={(row) => row.uuid}
                filters={filters}
                onFiltersChange={setFilters}
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={handleSort}
                showFilters={showFilters}
                showCheckbox={false}
                emptyMessage="Nenhum registro encontrado..."
            />

            <div className="sticky bottom-0 border-t border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 flex-shrink-0 z-10 px-4 py-3">
                <PageFooter
                    currentPage={currentPage}
                    totalPages={Math.ceil(totalElements / currentPageSize)}
                    itemsPerPage={currentPageSize}
                    totalElements={totalElements}
                    onPageChange={(p) => setCurrentPage(p)}
                    onItemsPerPageChange={(ps) => { setCurrentPageSize(ps); setCurrentPage(0); }}
                    itemsLabel="análises de crédito"
                />
            </div>

            {selectedAnalysis && <ApprovalModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} analysis={selectedAnalysis} />}
            {draweeModalDoc && (
                <UnifiedDraweeModal
                    batchId={draweeModalBatchId || ''}
                    itemId={draweeModalItemId || ''}
                    document={draweeModalDoc}
                    draweeName={draweeModalName || undefined}
                    onClose={() => { setDraweeModalDoc(null); setDraweeModalItemId(null); setDraweeModalName(null); setDraweeModalBatchId(null); }}
                />
            )}
        </div>
    );
}
